(() => {
  const elementName = 'cc-t10-specifications';
  const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

  if (customElements.get(elementName)) return;

  class CcT10Specifications extends HTMLElement {
    connectedCallback() {
      if (this.lifecycleController) return;

      this.lifecycleController = new AbortController();
      this.transitionTokens = new WeakMap();
      this.globalControl = this.querySelector('[data-cc-specifications-global]');
      this.groups = [...this.querySelectorAll('[data-cc-specifications-group]')]
        .map((group) => ({
          group,
          button: group.querySelector('[data-cc-specifications-toggle]'),
          panel: group.querySelector('[data-cc-specifications-panel]')
        }))
        .filter(({ button, panel }) => button instanceof HTMLButtonElement && panel instanceof HTMLElement);
      this.reducedMotionMedia = window.matchMedia(reducedMotionQuery);

      if (!(this.globalControl instanceof HTMLButtonElement) || this.groups.length !== 3) {
        this.destroy();
        return;
      }

      this.classList.add('cc-is-initializing');
      this.groups.forEach(({ button, panel }) => this.setImmediateState(button, panel, false));
      this.globalControl.hidden = false;
      this.updateGlobalControl();
      this.bindEvents();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.remove('cc-is-initializing'));
      });
    }

    disconnectedCallback() {
      this.destroy();
    }

    bindEvents() {
      const signal = this.lifecycleController.signal;

      this.groups.forEach(({ button, panel }) => {
        button.addEventListener('click', () => {
          const shouldExpand = button.getAttribute('aria-expanded') !== 'true';
          this.setGroupState(button, panel, shouldExpand);
        }, { signal });
      });

      this.globalControl.addEventListener('click', () => {
        const shouldExpand = !this.groups.every(({ button }) => button.getAttribute('aria-expanded') === 'true');
        this.groups.forEach(({ button, panel }) => this.setGroupState(button, panel, shouldExpand, false));
        this.updateGlobalControl();
      }, { signal });
    }

    setGroupState(button, panel, expanded, updateGlobal = true) {
      const token = (this.transitionTokens.get(panel) || 0) + 1;
      this.transitionTokens.set(panel, token);
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (this.reducedMotionMedia.matches) {
        this.setImmediateState(button, panel, expanded);
        if (updateGlobal) this.updateGlobalControl();
        return;
      }

      if (expanded) {
        panel.hidden = false;
        panel.removeAttribute('inert');
        panel.style.height = '0px';
        panel.style.opacity = '0';
        panel.getBoundingClientRect();
        panel.style.height = `${panel.scrollHeight}px`;
        panel.style.opacity = '1';
      } else {
        panel.hidden = false;
        panel.style.height = `${panel.getBoundingClientRect().height}px`;
        panel.style.opacity = '1';
        panel.getBoundingClientRect();
        panel.style.height = '0px';
        panel.style.opacity = '0';
      }

      const finish = () => {
        if (this.transitionTokens.get(panel) !== token) return;

        if (expanded) {
          panel.style.height = 'auto';
          panel.style.opacity = '';
        } else {
          panel.hidden = true;
          panel.setAttribute('inert', '');
          panel.style.height = '';
          panel.style.opacity = '';
        }
      };

      panel.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'height') finish();
      }, { once: true });

      window.setTimeout(finish, 360);
      if (updateGlobal) this.updateGlobalControl();
    }

    setImmediateState(button, panel, expanded) {
      this.transitionTokens.set(panel, (this.transitionTokens.get(panel) || 0) + 1);
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.hidden = !expanded;
      panel.toggleAttribute('inert', !expanded);
      panel.style.height = expanded ? 'auto' : '';
      panel.style.opacity = '';
    }

    updateGlobalControl() {
      const allExpanded = this.groups.every(({ button }) => button.getAttribute('aria-expanded') === 'true');
      this.globalControl.textContent = allExpanded ? '全部收合' : '全部展開';
      this.globalControl.setAttribute('aria-label', allExpanded ? '收合全部技術規格' : '展開全部技術規格');
    }

    destroy() {
      this.lifecycleController?.abort();
      this.lifecycleController = null;
    }
  }

  customElements.define(elementName, CcT10Specifications);
})();
